import React, { useState } from 'react'
import { Alert, App, Button, Input, Space, Table, Tag, Typography } from 'antd'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard } from '../../theme/listkit'
import { edgeErrorMessage } from '../../lib/edgeError'

/** Shape the `purge-test-users` Edge Function returns on a 2xx. */
export interface PurgeResult {
  ok: boolean
  dry_run: boolean
  matched: number
  deleted: number
  failed?: Array<{ user_id: string; step: string; error: string }>
  rejected: Array<{ user_id: string; email: string | null; reason: string }>
  sample: Array<{ user_id: string; name: string; email: string | null }>
  note?: string
}

/** The phrase the function requires, bound to the count the dry run reported. */
export const confirmPhrase = (count: number) => `DELETE ${count} TEST USERS`

/**
 * Test Data — permanently deletes the BrowserStack test accounts (first_name
 * prefixed `ZZ-`) and everything they own.
 *
 * The UI deliberately mirrors the Edge Function's brakes rather than trusting
 * them: the destructive button cannot be reached without first running a dry run,
 * and it stays disabled until the operator has typed the exact count-bound
 * phrase. Both are re-enforced server-side — this is a second lock on the same
 * door, not the only one.
 *
 * Any dry run is re-run after a delete, so the screen always shows current state
 * rather than a stale roster.
 */
export function PurgeTestUsersPage() {
  const { message } = App.useApp()
  const [result, setResult] = useState<PurgeResult | null>(null)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  /** A live delete is only offered once a dry run has actually reported a roster. */
  const [scanned, setScanned] = useState(false)

  const call = async (live: boolean) => {
    setBusy(true)
    try {
      const { data, error } = await supabaseClient.functions.invoke<PurgeResult>(
        'purge-test-users',
        { body: live ? { dry_run: false, confirm } : { dry_run: true } },
      )
      if (error) throw error
      setResult(data ?? null)
      if (live) {
        message.success(`Deleted ${data?.deleted ?? 0} test account(s)`)
        setConfirm('')
        // Re-scan so the roster on screen reflects reality after the delete.
        const { data: after } = await supabaseClient.functions.invoke<PurgeResult>(
          'purge-test-users',
          { body: { dry_run: true } },
        )
        if (after) setResult(after)
      } else {
        setScanned(true)
        message.info(`${data?.matched ?? 0} test account(s) matched`)
      }
    } catch (e) {
      message.error(await edgeErrorMessage(e, 'Request failed'))
    } finally {
      setBusy(false)
    }
  }

  const matched = result?.matched ?? 0
  const expected = confirmPhrase(matched)
  const canDelete = scanned && matched > 0 && confirm === expected && !busy

  return (
    <ListPage
      intro={{
        eyebrow: 'System · Maintenance',
        description:
          'Permanently remove the BrowserStack test accounts (first name prefixed ZZ-) and everything they own. Always dry-run first; the delete stays locked until you have seen the roster and typed the count-bound phrase.',
      }}
    >
      <SectionCard title="Purge ZZ- test accounts" style={{ maxWidth: 900 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Permanent, and not undoable"
            description={
              <>
                Deletes every profile whose first name starts <code>ZZ-</code>, their auth
                account, and everything that cascades from it — analytics events, settings,
                RSVPs, posts, badges — plus their GetStream chat user. Real members are not
                selected, and accounts on the never-delete list (including Apple&apos;s App
                Review account) are refused by the server even if they were somehow matched.
              </>
            }
          />

          <Space>
            <Button onClick={() => call(false)} loading={busy}>
              Run dry run
            </Button>
            <Typography.Text type="secondary">
              Always start here. Nothing is deleted by a dry run.
            </Typography.Text>
          </Space>

          {result && (
            <>
              <Space size="large">
                <Typography.Text>
                  Matched: <Typography.Text strong>{result.matched}</Typography.Text>
                </Typography.Text>
                <Typography.Text>
                  Deleted this run: <Typography.Text strong>{result.deleted}</Typography.Text>
                </Typography.Text>
                {result.rejected.length > 0 && (
                  <Tag color="gold">{result.rejected.length} rejected</Tag>
                )}
              </Space>

              {result.sample.length > 0 && (
                <Table
                  size="small"
                  rowKey="user_id"
                  pagination={false}
                  dataSource={result.sample}
                  columns={[
                    { title: 'Name', dataIndex: 'name' },
                    { title: 'Email', dataIndex: 'email' },
                  ]}
                  title={() => `Sample of matched accounts (${result.sample.length} of ${result.matched})`}
                />
              )}

              {result.rejected.length > 0 && (
                <Table
                  size="small"
                  rowKey="user_id"
                  pagination={false}
                  dataSource={result.rejected}
                  columns={[
                    { title: 'Email', dataIndex: 'email' },
                    { title: 'Why it was NOT selected', dataIndex: 'reason' },
                  ]}
                  title={() => 'Rejected — review these by hand'}
                />
              )}

              {result.failed && result.failed.length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  message={`${result.failed.length} account(s) failed`}
                  description={result.failed
                    .map((f) => `${f.user_id} (${f.step}): ${f.error}`)
                    .join('\n')}
                />
              )}
            </>
          )}

          {scanned && matched > 0 && (
            <SectionCard title="Confirm deletion">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>
                  Type <Typography.Text code copyable>{expected}</Typography.Text> to enable the
                  button. The phrase includes the count, so it stops working if the roster
                  changes.
                </Typography.Text>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={expected}
                  aria-label="Confirmation phrase"
                />
                <Button danger type="primary" disabled={!canDelete} loading={busy}
                  onClick={() => call(true)}>
                  Permanently delete {matched} test account(s)
                </Button>
              </Space>
            </SectionCard>
          )}
        </Space>
      </SectionCard>
    </ListPage>
  )
}
